import { useEffect, useState } from 'react';
import DialogoRedefinirSenha from '@/components/equipe/DialogoRedefinirSenha';
import Carregando from '@/components/Carregando';
import DialogoNovoMembro from '@/components/equipe/DialogoNovoMembro';
import { useAuth } from '@/auth/AuthProvider';
import {
  atualizarUsuario,
  definirExpediente,
  definirFuncoes,
  definirNoRodizio,
  mensagemDoErro,
  ouvirUsuariosDaClinica,
} from '@/services/usuariosService';
import DialogoHorario from '@/components/equipe/DialogoHorario';
import { ouvirClinica } from '@/services/clinicasService';
import { expedienteDaClinica, rotuloDoExpediente } from '@/produtividade/expediente';
import { PAPEIS, PAPEIS_DA_CLINICA } from '@/lib/constants';
import { FUNCOES, funcoesDe, podeTerFuncao, temFuncao } from '@/equipe/funcoes';

/** ★ VP — rótulos das FUNÇÕES (o que a pessoa faz no funil), separadas do papel (acesso). */
const ROTULO_FUNCAO = { sdr: 'SDR', negociador: 'Negociador' };
export const AVISO_FUNCAO_RECEPCAO = 'Recepção não recebe função de vendas — troque o acesso para Vendas primeiro.';
const AVISO_RODIZIO_RECEPCAO = 'Só quem atende o funil de vendas entra no rodízio de leads.';
/** Uma segunda qualquer, só para gerar o rótulo "08:00–17:00" da coluna Horário. */
const SEGUNDA_DE_EXEMPLO = '2026-10-12';

/**
 * ★★★ A-2 — QUEM TEM O PAPEL DE SDR E **NÃO** RECEBE LEADS NOVOS.
 *
 * ⚠⚠⚠ Este aviso existe por causa de um caso real: a **Damiana** entrou como SDR em outubro e
 * passou **dias sem receber um único lead** — ★ o rodízio dividia tudo entre Jéssica e Talita.
 * ★★★ Medido: a causa foi **cadastro**, não código. `noRodizio` **ausente** significa "fora", e
 * nada na tela dizia isso: a coluna mostrava "Não" como qualquer outro "Não".
 *
 * ⚠⚠⚠ **DEFEITO ACHADO NO CANÁRIO 4, em 10/out/2026 — e a regra mudou no mesmo dia.**
 *
 * ★ A primeira versão olhava o **PAPEL** (`papel === 'sdr'`), e o aviso saiu errado em produção:
 * *"1 SDR não recebe leads novos: **JOABE PIRES REINALDO**"*. ⚠⚠ O Joabe tem **acesso Vendas**
 * (papel `sdr`) e função **só Negociador** (`funcoes: ['negociador']`) — ★★★ e **negociador não
 * entra no rodízio**: a coluna "No rodízio" nem se aplica a ele.
 *
 * ★★ Decisão do dono (10/out): o aviso olha a **FUNÇÃO SDR**, por `temFuncao`/`funcoesDe`.
 * ⚠ `funcoesDe` **deriva do papel quando `funcoes` está ausente** — ★★★ e é isso que mantém o
 * caso que originou o aviso: a **Damiana** entrou com `funcoes` ausente e papel `sdr`, deriva
 * `['sdr']`, e **continua aparecendo** se sair do rodízio.
 *
 * ★ Quem sai do aviso com a troca: ⚠ **negociador puro** (o Joabe) e **gestor sem função SDR**
 * (em produção o gestor tem `funcoes: []` **explícito**, então ele já ficava fora).
 * ⚠⚠ Quem **passa a poder aparecer**: ★ um gestor com `funcoes` **ausente** — porque aí o
 * sistema inteiro o trata como SDR, e o aviso tem de concordar com o resto.
 *
 * ★ `noRodizio !== true` (e não `=== false`): ⚠⚠ é exatamente o estado da Damiana — **ausente**.
 * ★★★ Comparar com `false` deixaria de fora quem nunca foi marcado, que é o caso que dói.
 */
export const sdrsForaDoRodizio = (membros = []) =>
  (membros ?? []).filter((m) => temFuncao(m, 'sdr') && m?.ativo !== false && m?.noRodizio !== true);

export const avisoForaDoRodizio = (fora = []) => {
  if (fora.length === 0) return null;
  const nomes = fora.map((m) => m.nome || m.email || 'sem nome').join(', ');
  const plural = fora.length === 1 ? 'SDR não recebe' : 'SDRs não recebem';
  return `${fora.length} ${plural} leads novos: ${nomes}.`;
};

/**
 * Equipe da clínica (PLANO 3.2): quem tem acesso, com qual papel.
 *
 * Substitui o que era só possível por script: criar usuário com as claims de
 * tenancy certas. Papel e situação passam pelas callables, nunca por escrita
 * direta — trocar o papel exige mexer nas custom claims e revogar os tokens em
 * uso, e desativar exige desabilitar a conta no Auth. As rules bloqueiam esses
 * dois campos no cliente justamente por isso.
 */
export default function EquipePage() {
  const { clinicaId, usuario } = useAuth();
  const [membros, setMembros] = useState([]);
  // Quem está com o diálogo de senha aberto. Null = fechado.
  const [redefinindo, setRedefinindo] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(null);
  const [acaoErro, setAcaoErro] = useState(null);
  // Guarda o uid em operação: trava só a linha mexida, e não a tela inteira.
  const [ocupado, setOcupado] = useState(null);
  const [novoAberto, setNovoAberto] = useState(false);
  /** ★ A-2 — a pessoa cujo horário está aberto, ou `null`. */
  const [horarioDe, setHorarioDe] = useState(null);
  /**
   * ★★ O expediente da CLÍNICA é a reserva de quem não tem horário próprio — e o diálogo
   * precisa dele para mostrar *"segue o horário da clínica: 08:00–17:00"*.
   * ⚠ Sem ler a clínica, o diálogo cairia no `EXPEDIENTE_PADRAO` e mentiria para toda clínica
   * que configurou outro horário em Configurações → Desempenho.
   */
  const [expedienteClinica, setExpedienteClinica] = useState(expedienteDaClinica(null));

  useEffect(() => {
    if (!clinicaId) return undefined;
    return ouvirUsuariosDaClinica(
      clinicaId,
      (lista) => {
        setMembros(lista);
        setErro(null);
        setCarregando(false);
      },
      (e) => {
        console.error('[OdontoSDR] Falha ao listar a equipe:', e);
        setErro('Não foi possível carregar a equipe desta clínica.');
        setCarregando(false);
      },
    );
  }, [clinicaId]);

  useEffect(() => {
    if (!clinicaId) return undefined;
    return ouvirClinica(
      clinicaId,
      (c) => setExpedienteClinica(expedienteDaClinica(c?.config)),
      // ⚠ Falha aqui NÃO é erro de tela: o diálogo cai no padrão e segue editável.
      // ★ Mas vai para o registro, como manda a WT — listener silencioso foi o defeito da WT 1.
      (e) => console.error('[OdontoSDR] Falha ao ouvir o expediente da clínica:', e),
    );
  }, [clinicaId]);

  /** ★ A-2 — grava o horário da pessoa (ou apaga, para ela voltar a seguir a clínica). */
  async function salvarHorario(uid, expediente) {
    setAcaoErro(null);
    setOcupado(uid);
    try {
      await definirExpediente(clinicaId, uid, expediente);
    } catch (e) {
      console.error('[OdontoSDR] Falha ao salvar o horário:', e);
      setAcaoErro('Não foi possível salvar o horário. Só o gestor pode fazer isso.');
      throw e;
    } finally {
      setOcupado(null);
    }
  }

  async function executar(uid, mudancas) {
    setAcaoErro(null);
    setOcupado(uid);
    try {
      await atualizarUsuario({ clinicaId, uid, ...mudancas });
      // A lista reflete sozinha: o listener recebe a escrita do servidor.
    } catch (e) {
      console.error('[OdontoSDR] Falha ao atualizar membro:', e);
      setAcaoErro(mensagemDoErro(e, 'Não foi possível atualizar o membro.'));
    } finally {
      setOcupado(null);
    }
  }

  /**
   * ★ VP — marcar/desmarcar uma FUNÇÃO grava a lista EXPLÍCITA inteira (a partir
   * da atual, derivada ou não). Não passa pela callable: função não é claim; as
   * rules só deixam o gestor gravar e validam os valores.
   */
  /** ★ WA — entra/sai do rodízio de leads. Só o gestor (rules); a lista do sorteio é lida pelo servidor. */
  async function alternarRodizio(membro) {
    setAcaoErro(null);
    setOcupado(membro.id);
    try {
      await definirNoRodizio(clinicaId, membro.id, membro.noRodizio !== true);
    } catch (e) {
      console.error('[OdontoSDR] Falha ao mudar o rodízio:', e);
      setAcaoErro('Não foi possível mudar o rodízio. Só o gestor pode fazer isso.');
    } finally {
      setOcupado(null);
    }
  }

  async function alternarFuncao(membro, funcao) {
    const atuais = funcoesDe(membro);
    const novas = atuais.includes(funcao) ? atuais.filter((f) => f !== funcao) : [...atuais, funcao];
    setAcaoErro(null);
    setOcupado(membro.id);
    try {
      await definirFuncoes(clinicaId, membro.id, novas);
    } catch (e) {
      console.error('[OdontoSDR] Falha ao definir função:', e);
      setAcaoErro('Não foi possível alterar a função. Só o gestor pode fazer isso.');
    } finally {
      setOcupado(null);
    }
  }

  /** ★ A-2 — o aviso é derivado da lista, não de estado próprio: assim não pode ficar velho. */
  const avisoRodizio = avisoForaDoRodizio(sdrsForaDoRodizio(membros));

  if (carregando) return <Carregando texto="Carregando a equipe…" />;

  const ativos = membros.filter((m) => m.ativo !== false).length;

  return (
    <div className="pagina">
      <header className="pagina-cabecalho com-acao">
        <div>
          <h1>Equipe</h1>
          <p className="painel-sub">
            Quem tem acesso à plataforma e com qual papel.
          </p>
        </div>
        <button type="button" className="btn-primario compacto" onClick={() => setNovoAberto(true)}>
          Adicionar membro
        </button>
      </header>

      {erro && <p className="erro">{erro}</p>}
      {acaoErro && <p className="erro">{acaoErro}</p>}

      {/* ★★★ A-2 — O AVISO DO RODÍZIO, conserto de um caso real (a Damiana).
          ⚠⚠ Não é `erro`: pode ser configuração deliberada (férias, treinamento).
          ★ E traz o NOME de quem — senão o gestor tem de caçar a linha na tabela. */}
      {avisoRodizio && (
        <p className="aviso-carga" role="status" data-aviso="rodizio">
          {avisoRodizio}{' '}
          <span className="campo-ajuda">Marque “No rodízio” para incluir.</span>
        </p>
      )}

      <div className="indicadores">
        <div className="indicador">
          <span className="indicador-rotulo">Membros ativos</span>
          <span className="indicador-valor">{ativos}</span>
          {membros.length !== ativos && (
            <span className="indicador-nota">{membros.length - ativos} inativo(s)</span>
          )}
        </div>
      </div>

      <section className="secao">
        <div className="tabela-envolve">
          <table className="tabela">
            <thead>
              <tr>
                <th>Nome</th>
                <th>E-mail</th>
                <th>Acesso</th>
                <th>Funções</th>
                <th title="Quem recebe os leads novos, um a um, em ordem">No rodízio</th>
                <th title="Entrada, saída e almoço de cada dia — vale para a meta e o ritmo">Horário</th>
                <th>Situação</th>
                <th aria-label="Ações" />
              </tr>
            </thead>
            <tbody>
              {membros.map((m) => {
                const ativo = m.ativo !== false;
                const souEu = m.id === usuario?.uid;
                const travado = ocupado === m.id;

                return (
                  <tr key={m.id} className={ativo ? undefined : 'linha-inativa'}>
                    <td>
                      <strong>{m.nome || '—'}</strong>
                      {souEu && <span className="marca-voce">você</span>}
                    </td>
                    <td>{m.email || '—'}</td>
                    <td>
                      <select
                        value={m.papel}
                        aria-label={`Papel de ${m.nome || m.email}`}
                        /**
                         * Ninguém troca o PRÓPRIO papel por aqui, e isto o
                         * backend NÃO barra (ele só impede a autodesativação).
                         *
                         * Um gestor que se rebaixasse a Recepção perderia o
                         * acesso a esta tela no ato — a callable revoga os
                         * tokens para o papel novo valer na hora — e, se fosse
                         * o único gestor, a clínica ficaria sem ninguém capaz
                         * de administrar a equipe. Só um script com service
                         * account desfaria.
                         *
                         * Passar o bastão continua possível: promova outra
                         * pessoa a Gestor e peça que ela troque o seu papel.
                         */
                        disabled={travado || souEu}
                        title={souEu ? 'Você não pode trocar o próprio papel' : undefined}
                        onChange={(e) => executar(m.id, { papel: e.target.value })}
                      >
                        {/* Papel fora da lista (um superadmin que caiu aqui, ou
                            um valor antigo) aparece como opção própria: sem
                            isto o select mostraria outro papel, e um toque
                            distraído mudaria o acesso da pessoa. */}
                        {!PAPEIS_DA_CLINICA.includes(m.papel) && (
                          <option value={m.papel}>{PAPEIS[m.papel]?.label ?? m.papel}</option>
                        )}
                        {PAPEIS_DA_CLINICA.map((id) => (
                          <option key={id} value={id}>
                            {PAPEIS[id].label}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      {/* ★ VP — FUNÇÕES: o que a pessoa faz no funil. Recepção não
                          recebe função de vendas (não enxerga o funil): as caixas
                          ficam travadas com a explicação, e o caminho é trocar o
                          acesso primeiro. */}
                      <div className="funcoes-membro" aria-label={`Funções de ${m.nome || m.email}`}>
                        {FUNCOES.map((f) => (
                          <label key={f} className="check-inline">
                            <input
                              type="checkbox"
                              checked={funcoesDe(m).includes(f)}
                              disabled={travado || !podeTerFuncao(m)}
                              title={!podeTerFuncao(m) ? AVISO_FUNCAO_RECEPCAO : undefined}
                              onChange={() => alternarFuncao(m, f)}
                            />
                            {ROTULO_FUNCAO[f]}
                          </label>
                        ))}
                        {!podeTerFuncao(m) && <p className="campo-ajuda aviso-funcao">{AVISO_FUNCAO_RECEPCAO}</p>}
                      </div>
                    </td>
                    <td>
                      {/* ★ WA — RODÍZIO: marca EXPLÍCITA, ausente = fora. Não basta "ter a função SDR":
                          a função é derivada do papel quando ninguém a marcou, e o GESTOR derivaria `sdr`
                          — metade dos leads cairia no dono sem ninguém pedir (medido em produção). */}
                      <label className="check-inline" title={!podeTerFuncao(m) ? AVISO_RODIZIO_RECEPCAO : 'Recebe leads novos pelo rodízio'}>
                        <input
                          type="checkbox"
                          className="check-rodizio"
                          checked={m.noRodizio === true}
                          disabled={travado || !podeTerFuncao(m)}
                          onChange={() => alternarRodizio(m)}
                        />
                        {m.noRodizio === true ? 'Sim' : 'Não'}
                      </label>
                    </td>
                    <td>
                      {/* ★★ A-2 — o rótulo mostra o horário QUE VALE e diz de onde ele vem.
                          ⚠ "da clínica" em cinza é o padrão VISÍVEL: a lição do `noRodizio`
                          ausente da Damiana — estado implícito que ninguém vê custa dias. */}
                      <button
                        type="button"
                        className="btn-secundario compacto"
                        disabled={travado}
                        onClick={() => setHorarioDe(m)}
                        title={m.expediente ? 'Horário próprio' : 'Segue o horário da clínica'}
                      >
                        {rotuloDoExpediente(SEGUNDA_DE_EXEMPLO, m.expediente ?? expedienteClinica)}
                      </button>
                      {!m.expediente && <div className="campo-ajuda">da clínica</div>}
                    </td>
                    <td>
                      <span
                        className="pilula pequena"
                        style={{ background: ativo ? '#16a34a' : '#64748b' }}
                      >
                        {ativo ? 'Ativo' : 'Inativo'}
                      </span>
                    </td>
                    <td className="celula-acoes">
                      <button
                        type="button"
                        className="btn-secundario compacto"
                        // O servidor também barra (guardas.js), mas um botão que
                        // só falha ao ser clicado não explica nada. O title diz
                        // o porquê antes do clique.
                        disabled={travado || (souEu && ativo)}
                        title={
                          souEu && ativo
                            ? 'Você não pode desativar a própria conta'
                            : undefined
                        }
                        onClick={() => executar(m.id, { ativo: !ativo })}
                      >
                        {travado ? '…' : ativo ? 'Desativar' : 'Reativar'}
                      </button>
                      {/* Redefinir senha NÃO aparece na própria linha: revogar
                          os próprios tokens deslogaria o gestor na hora. Também
                          não aparece para superadmin — o servidor recusa de
                          qualquer forma, e oferecer o botão prometeria o que
                          não acontece. */}
                      {!souEu && m.papel !== 'superadmin' && (
                        <button
                          type="button"
                          className="btn-secundario compacto"
                          disabled={travado}
                          onClick={() => setRedefinindo(m)}
                        >
                          Redefinir senha
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {membros.length === 0 && (
            <p className="vazio">
              Nenhum membro cadastrado. Use “Adicionar membro” para dar acesso à primeira pessoa.
            </p>
          )}

          <DialogoRedefinirSenha
            clinicaId={clinicaId}
            membro={redefinindo}
            aberto={Boolean(redefinindo)}
            aoFechar={() => setRedefinindo(null)}
          />
        </div>
      </section>

      <DialogoHorario
        aberto={Boolean(horarioDe)}
        membro={horarioDe}
        expedienteDaClinica={expedienteClinica}
        aoFechar={() => setHorarioDe(null)}
        aoSalvar={(exp) => salvarHorario(horarioDe.id, exp)}
      />

      <DialogoNovoMembro
        aberto={novoAberto}
        aoFechar={() => setNovoAberto(false)}
        clinicaId={clinicaId}
      />
    </div>
  );
}
